import { useMutation, useQueryClient } from 'react-query';
import {
  ExperimentTemplateData,
  ExperimentTemplatePreview
} from '@experiment-details/types';
import {
  removeExpDetailsLocalTemplate,
  saveTemplateLocally
} from '../api/localExpDetailsViewActions';
import { expDetailsTemplateKeys } from '../api/exp-details-template-key-factory';
import useExpDetailsRouteParams from '../useExpDetailsRouteParams';
import {
  loadExpDetailsTemplate,
  saveExpDetailsTemplate
} from '../api/expDetailsTemplateApi';
import chartApi from '@/util/experimentDetails/chartApi';
import { cloneDeep } from 'lodash';
import { AUTO_GENERATED_OPTION_ID } from '@shared/constants';

type PrefetchSaveTemplateData = {
  templateId: string;
  formData: {
    name?: string;
    isDefault?: boolean;
  };
  onlyLocal?: boolean;
};

type SaveTemplateData = {
  templateData: ExperimentTemplateData;
  formData?: {
    name?: string;
    isDefault?: boolean;
  };
  onlyLocal?: boolean;
};

const shouldPrefetchTemplate = (
  data: PrefetchSaveTemplateData | SaveTemplateData
): data is PrefetchSaveTemplateData => 'templateId' in data;

type SaveTemplateOpts = {
  data: SaveTemplateData | PrefetchSaveTemplateData;
  projectId: string;
};
const saveTemplate = async ({ data, projectId }: SaveTemplateOpts) => {
  const shouldPrefetch = shouldPrefetchTemplate(data);

  let templateData: ExperimentTemplateData;

  if (shouldPrefetch) {
    templateData = await loadExpDetailsTemplate({
      templateId: data.templateId,
      projectId
    });
  } else {
    templateData = cloneDeep(data.templateData);
  }

  if (data.formData?.name) {
    templateData.chartTemplate.template_name = data.formData.name;
  }

  if (data.onlyLocal) {
    await saveTemplateLocally(templateData);

    return templateData;
  }

  // It won't delete the local view on auto-saving the auto-generated view
  if (templateData.isLocalTemplate) {
    await removeExpDetailsLocalTemplate(
      templateData.projectId,
      templateData.templateId || AUTO_GENERATED_OPTION_ID
    );
  }

  const res = await saveExpDetailsTemplate(templateData);

  const isDefaultPayload = data.formData?.isDefault;
  const isDefaultChanged =
    isDefaultPayload !== undefined &&
    isDefaultPayload !== res.data.projectDefault;

  if (isDefaultChanged) {
    await chartApi.setProjectTemplate({
      project_id: templateData.projectId,
      template_id: isDefaultPayload ? templateData.templateId : null
    });
  }

  return res.data;
};

export default function useSaveExpDetailsTemplateMutation() {
  const { projectId } = useExpDetailsRouteParams();
  const queryClient = useQueryClient();

  return useMutation(
    (data: SaveTemplateData | PrefetchSaveTemplateData) =>
      saveTemplate({ data, projectId }),
    {
      onMutate: async data => {
        const listQueryKey = expDetailsTemplateKeys.templateList(projectId);
        const prevData: ExperimentTemplatePreview[] =
          queryClient.getQueryData(listQueryKey) || [];

        const templateId = shouldPrefetchTemplate(data)
          ? data.templateId
          : data.templateData.templateId;

        const updatedList = prevData.map(t => {
          if (t.templateId !== templateId) return t;

          let defaultTemplate = t.defaultTemplate;
          const isDefault = data.formData?.isDefault;

          if (isDefault !== undefined && isDefault !== defaultTemplate) {
            defaultTemplate = isDefault;
          }

          return {
            ...t,
            templateName: data.formData?.name || t.templateName,
            defaultTemplate
          };
        });

        queryClient.setQueryData(listQueryKey, updatedList);

        return { prevData };
      },
      onError: (e, _, context) => {
        queryClient.setQueryData(
          expDetailsTemplateKeys.templateList(projectId),
          context?.prevData
        );
      }
    }
  );
}
